// @flow
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { putProfileApi, uploadImageApi } from './api';
import { actions as AuthActions } from '../../auth/duck';
import { setNewAlert } from '../../alert/duck';
import update from 'immutability-helper';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../../error/index';
import type { UploadImageResponse } from './api';
import type { Language } from '../../../entities';
import { ofType } from 'redux-observable';
import { dataURItoBlob } from '../../../utils/file';

// review
export type EditProfileAction = {
  +type: string;
  +profileData?: ProfileData;
  +error?: HTTPError;
};

export type ProfileData = {
  +image?: string;
  imageURI?: string;
  +email?: string;
  +url?: string;
  +lastName?: string;
  +firstName?: string;
  +isPhoneVerified?: boolean;
  +languageCode?: Language;
};

export type EditProfileState = {
  +isPutting?: boolean;
  +isUploadingImage?: boolean;
  +dataEditSuccessful?: boolean;
  +putError?: HTTPError;
};

//Actions
const EDIT_PROFILE_PUT_PROFILE_REQUEST = 'EDIT_PROFILE_PUT_PROFILE_REQUEST';
const EDIT_PROFILE_PUT_PROFILE_SUCCESS = 'EDIT_PROFILE_PUT_PROFILE_SUCCESS';
const EDIT_PROFILE_PUT_PROFILE_ERROR = 'EDIT_PROFILE_PUT_PROFILE_ERROR';
const EDIT_PROFILE_PUT_IMAGE_REQUEST = 'EDIT_PROFILE_PUT_IMAGE_REQUEST';
const EDIT_PROFILE_PUT_IMAGE_SUCCESS = 'EDIT_PROFILE_PUT_IMAGE_SUCCESS';
const EDIT_PROFILE_PUT_IMAGE_ERROR = 'EDIT_PROFILE_PUT_IMAGE_ERROR';
const EDIT_PROFILE_RESET_STATE = 'EDIT_PROFILE_RESET_STATE';

//Action creators
export function editProfile(profileData: ProfileData): EditProfileAction {
  return {
    type: EDIT_PROFILE_PUT_PROFILE_REQUEST,
    profileData,
  };
}

export function editProfileSuccess(): EditProfileAction {
  return {
    type: EDIT_PROFILE_PUT_PROFILE_SUCCESS,
  }
}

export function editProfileError(error: HTTPError): EditProfileAction {
  return {
    type: EDIT_PROFILE_PUT_PROFILE_ERROR,
    error,
  }
}

export function editProfileImage(profileData: ProfileData): EditProfileAction {
  return {
    type: EDIT_PROFILE_PUT_IMAGE_REQUEST,
    profileData,
  }
}

export function editProfileImageSuccess(profileData: ProfileData): EditProfileAction {
  return {
    type: EDIT_PROFILE_PUT_IMAGE_SUCCESS,
    profileData,
  }
}

export function editProfileImageError(error: HTTPError): EditProfileAction {
  return {
    type: EDIT_PROFILE_PUT_IMAGE_ERROR,
    error,
  }
}

export function resetEditProfileState(): EditProfileAction {
  return {
    type: EDIT_PROFILE_RESET_STATE,
  }
}

export const actions = {
  EDIT_PROFILE_PUT_PROFILE_REQUEST,
  EDIT_PROFILE_PUT_PROFILE_SUCCESS,
  EDIT_PROFILE_PUT_PROFILE_ERROR,
  EDIT_PROFILE_PUT_IMAGE_REQUEST,
  EDIT_PROFILE_PUT_IMAGE_SUCCESS,
  EDIT_PROFILE_PUT_IMAGE_ERROR,
  EDIT_PROFILE_RESET_STATE,
  editProfile,
  editProfileError,
  editProfileSuccess,
  editProfileImage,
  editProfileImageError,
  resetEditProfileState,
};

export const initialState: EditProfileState = {};

//Reducer
export default function reducer(state: EditProfileState = initialState, action: EditProfileAction): EditProfileState {
  switch (action.type) {
    case EDIT_PROFILE_PUT_PROFILE_REQUEST:
      return {
        isPutting: true,
      };
    case EDIT_PROFILE_PUT_PROFILE_SUCCESS:
      return {
        dataEditSuccessful: true,
      };
    case EDIT_PROFILE_PUT_PROFILE_ERROR:
      return {
        putError: action.error,
      };
    case EDIT_PROFILE_PUT_IMAGE_REQUEST:
      return {
        isUploadingImage: true,
      };
    /*case EDIT_PROFILE_PUT_IMAGE_SUCCESS:
      return {
        imageId: action.imageId,
      };*/
    case EDIT_PROFILE_PUT_IMAGE_ERROR:
      return {
        putError: action.error,
      };
    // If there'll be more properties they will not be changed
    case EDIT_PROFILE_RESET_STATE:
      return update(state, { $unset: ['dataEditSuccessful'] });
    default:
      return state;
  }
}

//Epics
export function editImageProfileEpic(action$: Observable<EditProfileAction>): Observable<EditProfileAction> {
  return action$.pipe(
    ofType(EDIT_PROFILE_PUT_IMAGE_REQUEST),
    mergeMap(({ profileData }) => {
      const fd = new FormData();
      fd.append('file', dataURItoBlob(profileData.imageURI));
      fd.append('resourceGroup', 'USER');
      return uploadImageApi(fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).pipe(
        map((uploadImageResponse: UploadImageResponse) => editProfileImageSuccess(
          update(profileData, {
            imageId: { $set: uploadImageResponse.id },
            $unset: ['imageURI']
          })
        )),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(editProfileImageError(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(editProfileImageError(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(editProfileImageError(e));
        }))
    }));
}

export function editProfileEpic(action$: Observable<EditProfileAction>): Observable<EditProfileAction> {
  return action$.pipe(
    ofType(EDIT_PROFILE_PUT_PROFILE_REQUEST, EDIT_PROFILE_PUT_IMAGE_SUCCESS),
    mergeMap(({ profileData }) => putProfileApi(profileData).pipe(
      mergeMap((profile: ProfileData) => of(editProfileSuccess(), AuthActions.updateUser(profile))),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(editProfileError(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(editProfileError(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(editProfileError(e));
      }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/preferences/edit-profile/duck.js